import { useState, useEffect } from 'react';
import { ChevronDown, FileText } from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useSearchParams, useNavigate } from 'react-router';
import { apiFetch } from '../lib/api-client';

interface Resume {
  id: number;
  name: string;
  filename: string;
  created_at: string;
  parse_status: string;
}

interface ResumeSwitcherProps {
  className?: string;
}

export function ResumeSwitcher({ className = '' }: ResumeSwitcherProps) {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Load resumes on mount
  useEffect(() => {
    loadResumes();
  }, []);

  // Handle resumeId from URL params or localStorage
  useEffect(() => {
    const urlResumeId = searchParams.get('resumeId');
    const storedResumeId = localStorage.getItem('selectedResumeId');
    
    if (resumes.length > 0) {
      let targetResumeId = urlResumeId || storedResumeId;
      
      if (targetResumeId) {
        const resume = resumes.find(r => r.id.toString() === targetResumeId);
        if (resume) {
          setSelectedResume(resume);
          return;
        }
      }
      
      // Default to most recent resume
      const mostRecent = resumes.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
      
      if (mostRecent) {
        setSelectedResume(mostRecent);
        // Update URL and localStorage
        updateResumeContext(mostRecent.id.toString());
      }
    }
  }, [resumes, searchParams]);

  const loadResumes = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/resumes');
      const data = await response.json();
      
      if (data.success) {
        setResumes(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load resumes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateResumeContext = (resumeId: string) => {
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.set('resumeId', resumeId);
    setSearchParams(newParams);
    
    // Update localStorage
    localStorage.setItem('selectedResumeId', resumeId);
  };

  const handleResumeSelect = (resume: Resume) => {
    setSelectedResume(resume);
    updateResumeContext(resume.id.toString());
    
    // Trigger page refresh for data reload
    window.location.reload();
  };

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="w-4 h-4 bg-muted animate-pulse rounded"></div>
        <div className="w-24 h-4 bg-muted animate-pulse rounded"></div>
      </div>
    );
  }

  if (resumes.length === 0) {
    return (
      <div className={`text-sm text-muted-foreground ${className}`}>
        No resumes available
      </div>
    );
  }

  return (
    <Menu as="div" className={`relative ${className}`}>
      <MenuButton className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-accent transition-colors">
        <FileText className="w-4 h-4 text-muted-foreground" />
        <span className="max-w-32 truncate">
          {selectedResume?.filename || 'Select Resume'}
        </span>
        <ChevronDown className="w-4 h-4 text-muted-foreground" />
      </MenuButton>

      <MenuItems className="absolute right-0 mt-2 w-64 bg-popover border border-border rounded-lg shadow-lg z-50 py-1">
        {resumes.map((resume) => (
          <MenuItem key={resume.id}>
            {({ focus }) => (
              <button
                onClick={() => handleResumeSelect(resume)}
                className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                  focus ? 'bg-accent' : ''
                } ${
                  selectedResume?.id === resume.id ? 'bg-accent/50 font-medium' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {resume.filename}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(resume.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="ml-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      resume.parse_status === 'completed' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : resume.parse_status === 'failed'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                    }`}>
                      {resume.parse_status === 'completed' ? 'Ready' : 
                       resume.parse_status === 'failed' ? 'Error' : 'Processing'}
                    </span>
                  </div>
                </div>
              </button>
            )}
          </MenuItem>
        ))}
        
        <div className="border-t border-border mt-1 pt-1">
          <MenuItem>
            {({ focus }) => (
              <button
                onClick={() => navigate('/resumes')}
                className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                  focus ? 'bg-accent' : ''
                } text-muted-foreground`}
              >
                Manage Resumes →
              </button>
            )}
          </MenuItem>
        </div>
      </MenuItems>
    </Menu>
  );
}